import { useState, useEffect } from 'react';
import { Link } from 'react-router';
import { sentinelTicketPath } from '@/lib/landing';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { followupsService } from '@/services/followups.service';
import { followupStateText } from './followup-copy';
import { canSendFollowup, followupActionError, followupEventText, followupWhen } from './followup-format';
import { describePendingAction } from '../sentinel-workspace.helpers';
import type { FollowupRow } from './types';

export function When({ iso }: { iso: string | null }) {
  const when = iso ? followupWhen(iso) : null;
  if (!when) return <span className="text-gray-700">—</span>;
  return (
    <time dateTime={iso!} title={when.absolute}>
      {when.relative} <span className="text-gray-700">({when.absolute})</span>
    </time>
  );
}

export function FollowupRowItem({
  row,
  canEdit,
  onOpen,
  onChanged,
  showDetails = true,
  showDraft = true,
}: {
  row: FollowupRow;
  canEdit: boolean;
  onOpen: (id: string) => void;
  onChanged: () => void;
  /** Off where the details are already beside the row, as in the Follow-ups view. */
  showDetails?: boolean;
  /** Off where the draft is shown and sent under the ticket's messages, as in the Follow-ups view. */
  showDraft?: boolean;
}) {
  const [confirmingCancel, setConfirmingCancel] = useState(false);
  // One-shot: a confirmation opened before the row started sending (or ended) is dropped, so it can
  // never reappear later and cancel without a fresh click.
  const cancelBlocked = row.state === 'sending' || row.state === 'ended';
  useEffect(() => {
    if (cancelBlocked) setConfirmingCancel(false);
  }, [cancelBlocked]);
  const [draftText, setDraftText] = useState(row.draft?.text ?? '');
  // The list polls: when a new draft arrives for this row, show it in place of the old text or edits.
  const draftKey = row.draft ? `${row.draft.receivedAt}|${row.draft.text}` : '';
  const [draftShown, setDraftShown] = useState(draftKey);
  if (draftShown !== draftKey) {
    setDraftShown(draftKey);
    setDraftText(row.draft?.text ?? '');
  }
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const idBase = `followup-${row.id}`;
  const ended = row.state === 'ended';
  // Shadow drafts exist for review only: they are never offered for sending.
  const canSend = showDraft && canSendFollowup(row, canEdit);

  const run = async (action: () => Promise<unknown>, fallback: string) => {
    setBusy(true);
    setError(null);
    try {
      await action();
      setConfirmingCancel(false);
      onChanged();
    } catch (err) {
      setError(followupActionError(err, fallback));
    } finally {
      setBusy(false);
    }
  };

  const customer = row.customer.name ?? row.customer.email ?? 'Unknown customer';
  const manual = row.origin === 'manual';
  const rule = manual ? 'Added manually' : `${row.workflow.name ?? 'Unnamed workflow'} · step ${row.step.index} of ${row.step.total}`;

  return (
    <li
      aria-labelledby={`${idBase}-title`}
      className="flex flex-col gap-2 rounded-2xl border border-gray-500 bg-white px-4 py-3"
      onClick={(event) => {
        // The row opens its details; its own links, buttons and fields keep their clicks.
        if ((event.target as HTMLElement).closest('a, button, textarea, input, select')) return;
        onOpen(row.id);
      }}
    >
      <div className="flex flex-wrap items-center gap-x-3 gap-y-1">
        <p id={`${idBase}-title`} className="text-[13px] font-medium leading-[18px] text-text-body">
          {row.sentinelTicketId !== null ? (
            <Link
              to={sentinelTicketPath(row.sentinelTicketId)}
              className="underline-offset-2 hover:underline focus-visible:rounded focus-visible:outline-2 focus-visible:outline-ring"
            >
              Ticket #{row.ticketId}
            </Link>
          ) : (
            <>Ticket #{row.ticketId}</>
          )}
          <span className="font-normal text-gray-800"> · {customer}</span>
        </p>
        <p className="text-[12px] leading-4 text-gray-800">
          {rule}
        </p>
        {row.workflow.changed && <Badge variant="warning">Changed since scheduled</Badge>}
        <Badge variant={ended ? 'secondary' : row.state === 'held' ? 'warning' : 'outline'}>{followupStateText(row)}</Badge>
        {row.mode === 'shadow' && <Badge variant="secondary">Shadow</Badge>}
      </div>

      <div className="flex flex-wrap gap-x-6 gap-y-1 text-[12px] leading-4 text-gray-800">
        {!ended && (
          <p>
            Next due: <When iso={row.nextDueAt} />
          </p>
        )}
        {/* No flex-1: in a narrow column it wraps to its own line instead of squeezing to a word per line. */}
        {row.lastEvent && <p className="min-w-0">{followupEventText(row.lastEvent.text)}</p>}
      </div>

      {showDraft && row.state === 'queued' && row.draft && (
        <div className="flex flex-col gap-1.5">
          <label htmlFor={`${idBase}-draft`} className="text-[12px] font-medium text-gray-700">
            Follow-up draft
          </label>
          {row.draft.blocked && (
            <p id={`${idBase}-blocked`} role="note" className="rounded-lg bg-amber-50 px-3 py-2 text-[12px] leading-4 text-amber-800">
              Kim's safety check blocked this follow-up: {row.draft.blocked.reason}.{' '}
              {row.draft.text ? 'Fix it or rewrite it, then send it yourself.' : 'Write the message you want to send.'}
            </p>
          )}
          <Textarea
            id={`${idBase}-draft`}
            value={draftText}
            readOnly={!canSend}
            placeholder="Write the follow-up to send"
            aria-describedby={row.draft.blocked ? `${idBase}-blocked` : undefined}
            onChange={(event) => setDraftText(event.target.value)}
            className="text-[13px] focus-visible:ring-2 focus-visible:ring-ring/50"
          />
          {row.mode === 'shadow' && (
            <p className="text-[11px] leading-4 text-gray-800">Shadow mode: this draft is for review and will not be sent.</p>
          )}
          {/* Send runs these too, so the Sentinel sees them before pressing it. */}
          {row.draft.actions && row.draft.actions.length > 0 && (
            <div className="flex flex-col gap-1" id={`${idBase}-actions`}>
              <p className="text-[12px] font-medium text-gray-700">{row.mode === 'shadow' ? 'Would also' : 'Send also'}</p>
              <ul className="flex flex-col gap-0.5 text-[12px] leading-4 text-text-body">
                {row.draft.actions.map((action, index) => {
                  const { label, value } = describePendingAction(action);
                  return (
                    <li key={`${action.tool}-${index}`}>
                      {label}
                      {value && <span className="text-gray-700"> · {value}</span>}
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
        </div>
      )}

      {error && (
        <p role="alert" className="text-[12px] leading-4 text-red-600">
          {error}
        </p>
      )}

      <div className="flex flex-wrap items-center justify-end gap-2">
        {showDetails && (
          <Button variant="outline" size="sm" onClick={() => onOpen(row.id)} aria-describedby={`${idBase}-title`}>
            Details
          </Button>
        )}
        {canSend && (
          <Button
            size="sm"
            disabled={busy || !draftText.trim()}
            onClick={() => void run(() => followupsService.send(row.id, draftText), 'Could not send the follow-up.')}
            // A screen reader hears what else Send does, as a sighted Sentinel sees it above.
            aria-describedby={row.draft?.actions?.length ? `${idBase}-title ${idBase}-actions` : `${idBase}-title`}
          >
            Send
          </Button>
        )}
        {/* Past the point of sending, a follow-up is going out: it cannot be cancelled. */}
        {canEdit && !ended && row.state !== 'sending' && !confirmingCancel && (
          <Button variant="outline" size="sm" disabled={busy} onClick={() => setConfirmingCancel(true)} aria-describedby={`${idBase}-title`}>
            Cancel
          </Button>
        )}
        {/* A confirmation left open when the row turns to sending must not still cancel it. */}
        {confirmingCancel && row.state !== 'sending' && !ended && (
          <div role="group" aria-label="Confirm cancel" className="flex items-center gap-2">
            <span className="text-[12px] text-text-body">Cancel this follow-up?</span>
            <Button
              variant="destructive"
              size="sm"
              disabled={busy}
              onClick={() => void run(() => followupsService.cancel(row.id), 'Could not cancel the follow-up.')}
            >
              Yes, cancel
            </Button>
            <Button variant="outline" size="sm" disabled={busy} onClick={() => setConfirmingCancel(false)}>
              Keep
            </Button>
          </div>
        )}
      </div>
    </li>
  );
}
